import { Route, Routes, createBrowserRouter } from 'react-router-dom';
import { PAGES } from 'constants/pages';

import { AuthGuard } from 'components/guards/AuthGuard';
import { GuestGuard } from 'components/guards/GuestGuard';
import { PrivateLayout } from './PrivateLayout';

// Public pages (no auth required)
import { SupplyOrderConfirmation } from './SupplyOrders/SupplyOrderConfirmation';
import { AdvertisementScreenConfigLoggedOut } from './AdvertisementScreenConfig/AdvertisementScreenConfigLoggedOut';

// Guest-only pages (redirect to home if already logged in)
import { Login } from './Login';
import { SetPassword } from './SetPassword/SetPassword';
import { ForgotPassword } from './ForgotPassword/ForgotPassword';
import { ResetPassword } from './ForgotPassword/ResetPassword';
import { PasswordDefined } from './PasswordDefined/PasswordDefined';

// Private pages
import { AdvertisementScreenConfig } from 'pages/AdvertisementScreenConfig';
import { BadgeCreate } from './Badges/BadgeCreate';
import { BadgeUpdate } from './Badges/BadgeUpdate';
import { Badges } from './Badges';
import { BakeryRecords } from 'pages/BakeryRecords';
import { Bundles } from 'pages/Bundles';
import { BundleDetails } from 'pages/Bundles/BundleDetails';
import { Categories } from 'pages/Categories';
import { CategoryDetails } from 'pages/Categories/CategoryDetails';
import { CommentsPage } from 'pages/Comments';
import { CreateCategory } from 'pages/Categories/CreateCategory';
import { CreateToteamUser } from 'pages/Users/CreateToteamUser';
import { CreditsList, CreditsForm, CreditDetails } from './Credits';
import { Deliveries } from './Deliveries';
import { DeliveriesDailyPhotos } from 'pages/Deliveries/DeliveriesDailyPhotos';
import { DeliveryDetails } from './Deliveries/DeliveryDetails';
import { DeliveryPlanning } from 'pages/DeliveryPlanning';
import { DeliveryPlanningAudit } from 'pages/DeliveryPlanning/DeliveryPlanningAudit';
import { DynamicDeliveryPlanning } from 'pages/DynamicDeliveryPlanning';
import { Tournees } from 'pages/Tournees';
import { TourDetails } from 'pages/Tournees/TourDetails';
import { Equipments } from 'pages/Equipments';
import { EquipmentCreate } from 'pages/Equipments/EquipmentCreate';
import { EquipmentDetails } from 'pages/Equipments/EquipmentDetails';
import { ExpiredFutureStockVariations } from 'pages/Stocks/ExpiredFutureStockVariations';
import { ExpiryDateRecords } from 'pages/ExpiryDateRecords';
import { FutureStockVariations } from 'pages/Stocks/FutureStockVariations';
import { Home } from './Home';
import { HotDrinksRecipes } from 'pages/HotDrinksRecipes';
import { HotDrinksRecipeDetails } from 'pages/HotDrinksRecipes/details';
import { Locations } from 'pages/Locations';
import { ManualSupplyOrders } from 'pages/ManualSupplyOrders';
import { MessagesSet } from 'pages/MessagesSets/index';
import { MessagesSetCreate } from 'pages/MessagesSets/MessagesSetCreate';
import { MessagesSetDetails } from 'pages/MessagesSets/MessagesSetDetails';
import { MicrostoreColumnDetails } from 'pages/MicrostoreColumns/MicrostoreColumnDetails';
import { MicrostoreColumnGroupDetails } from 'pages/MicrostoreColumnGroups/MicrostoreColumnGroupDetails';
import { MicrostoreColumnGroups } from 'pages/MicrostoreColumnGroups';
import { MicrostoreColumnTemplateDetails } from 'pages/MicrostoreColumnTemplates/MicrostoreColumnTemplateDetails';
import { MicrostoreColumnTemplates } from 'pages/MicrostoreColumnTemplates';
import { MicrostoreColumns } from 'pages/MicrostoreColumns';
import { MicrostoreColumnsGroupTargetsInfo } from 'pages/MicrostoreColumnGroups/TargetsInfo';
import { MicrostoreMenuProductArrangements } from 'pages/MicrostoreMenus/MicrostoreMenuProductArrangements';
import { MicrostoreMenuQuantities } from 'pages/MicrostoreMenus/MicrostoreMenuQuantities';
import { MicrostoreMenus } from 'pages/MicrostoreMenus';
import { MicrostoreMenusDetails } from 'pages/MicrostoreMenus/MicrostoreMenusDetails';
import { MicrostorePlanogram } from 'pages/MicrostorePlanogram';
import { MicrostoreSnapshot } from 'pages/MicrostoreSnapshot';
import { MealPlanDefinitions } from 'pages/MealPlanDefinitions';
import { MealPlanDefinitionCreate } from 'pages/MealPlanDefinitions/MealPlanDefinitionCreate';
import { MealPlanDefinitionDetails } from 'pages/MealPlanDefinitions/MealPlanDefinitionDetails';
import { MobilePopupsPage } from 'pages/MobilePopups';
import { NayaxMDBTemplateDetails } from 'pages/NayaxMDBTemplates/NayaxMDBTemplateDetails';
import { NayaxMDBTemplates } from 'pages/NayaxMDBTemplates';
import { NotFound } from 'components/NotFound';
import { Notifications } from './Notifications';
import { NotificationsOrganization } from './Notifications/NotificationsOrganization';
import { NotificationsSite } from 'pages/Notifications/NotificationsSite';
import { NotificationsUser } from './Notifications/NotificationsUser';
import { Basket } from 'pages/Basket';
import { OpsParameters } from 'pages/OpsParameters';
import { DeliveryZones } from 'pages/DeliveryZones';
import { PermissionGroups } from 'pages/PermissionGroups';
import { PermissionGroupDetails } from 'pages/PermissionGroups/PermissionGroupDetails';
import { PriceInvisibilityRules } from 'pages/PriceInvisibilityRules';
import { PriceInvisibilityRuleDetails } from 'pages/PriceInvisibilityRules/PriceInvisibilityRuleDetails';
import { DeliveryZoneDetails } from 'pages/DeliveryZones/DeliveryZoneDetails';
import { OrderCreate } from 'pages/Orders/OrderCreate';
import { OrderDetails } from 'pages/Orders/OrderDetails';
import { Orders } from 'pages/Orders';
import { OrganizationCreate } from './Organizations/OrganizationCreate';
import { OrganizationDetails } from './Organizations/OrganizationDetails';
import { Organizations } from './Organizations';
import { Totems } from './Totems';
import { TotemDetails } from './Totems/TotemDetails';
import { ColumnGroupPage } from './MicrostoreColumnGroups/ColumnGroupPage';
import { PollCreate } from 'pages/Polls/PollCreate';
import { PollDetails } from 'pages/Polls/PollDetails';
import { Polls } from 'pages/Polls';
import { ProductArrangementDetails } from 'pages/ProductArrangements/ProductArrangementDetails';
import { ProductArrangements } from 'pages/ProductArrangements';
import { ProductCreate } from 'pages/Products/ProductCreate';
import { ProductCsvImport } from './Products/ProductImports/index';
import { ProductDetails } from './Products/ProductDetails';
import { ProductGroupDetails } from 'pages/ProductGroup/details/index.ts';
import { ProductPriceRangeCreate } from './ProductPriceRanges/ProductPriceRangeCreate';
import { ProductPriceRangeDetails } from './ProductPriceRanges/ProductPriceRangeDetails';
import { ProductPriceRanges } from './ProductPriceRanges';
import { Products } from './Products';
import { QuizCreate } from 'pages/Quizzes/QuizCreate';
import { QuizDetails } from 'pages/Quizzes/QuizDetails';
import { Quizzes } from 'pages/Quizzes';
import { RitualCreate } from 'pages/Rituals/RitualCreate';
import { RitualDetails } from 'pages/Rituals/RitualDetails';
import { RitualTemplates } from 'pages/RitualTemplates';
import { Rituals } from 'pages/Rituals';
import { Settings } from './Settings';
import { SiteCreate } from './Sites/SiteCreate';
import { SiteCsvImport } from './Sites/SiteImports/index';
import { SiteDetails } from './Sites/SiteDetails';
import { SiteMicrostoreColumnGroups } from './Sites/SiteMicrostoreColumnGroups';
import { SiteExternalInventory } from 'pages/SiteExternalInventory';
import { SiteInventoryStockCreate } from './Stocks/SiteInventoryStockCreate';
import { SiteSetupDetails } from 'pages/SiteSetups/details';
import { SiteSetups } from 'pages/SiteSetups';
import { SiteStocksOverview } from './Stocks/SiteStocksOverview';
import { ColumnStocks } from './Stocks/ColumnStocks';
import { ColumnProductStocks } from './Stocks/ColumnProductStocks';
import { Sites } from './Sites';
import { StockTransferCreate } from './StockTransfers/StockTransferCreate';
import { StockTransferDetails } from './StockTransfers/StockTransferDetails';
import { StockTransferReturns } from 'pages/StockTransfers/StockTransferReturns';
import { StockTransfers } from './StockTransfers';
import { StockVariationCreate } from './Stocks/StockVariationCreate';
import { StockVariationMultiCreate } from 'pages/Stocks/StockVariationMultiCreate';
import { StockVariationsByDay } from 'pages/Stocks/variationsByDay';
import { Stocks } from './Stocks';
import { SubventionCreate } from 'pages/Subventions/SubventionCreate';
import { SubventionDetails } from 'pages/Subventions/SubventionDetails';
import { Subventions } from 'pages/Subventions';
import { SupplierCreate } from 'pages/Suppliers/SupplierDetails/SupplierCreate';
import { SupplierDetails } from './Suppliers/SupplierDetails';
import { SupplierDiscountsPage } from 'pages/SupplierDiscounts';
import { SupplierPartnerships } from 'pages/SupplierPartnership';
import { Suppliers } from './Suppliers';
import { SupplyOrderDetails } from './SupplyOrders/SupplyOrderDetails';
import { SupplyOrders } from './SupplyOrders';
import { TagCreate } from './Tags/TagCreate';
import { TagUpdate } from './Tags/TagUpdate';
import { Tags } from './Tags';
import { Tickets } from './Tickets';
import { TicketDetails } from './Tickets/TicketDetails';
import { TermsRecordCreate } from './TermsRecords/TermsRecordCreate';
import { TermsRecords } from './TermsRecords';
import { Thermometers } from 'pages/Thermometers';
import { TotemClosedDays } from 'pages/TotemClosedDays';
import { TotemClosedReceptionDays } from 'pages/TotemClosedReceptionDays';
import { TotopsUserCreated } from 'pages/Users/TotopsUserCreated';
import { TransactionDetails } from './Transactions/TransactionDetails';
import { Audit } from './Audit';
import { AuditDetails } from './Audit/AuditDetails';
import { Ntfy } from './Ntfy';
import { Transactions } from './Transactions';
import { Payments } from './Payments';
import { PaymentDetails } from './Payments/PaymentDetails';
import { PaymentMethodDetails } from './Payments/PaymentMethodDetails';
import { TrayCapacities } from 'pages/TrayCapacities';
import { UrbantzRoundPresets } from 'pages/UrbantzRoundPresets';
import { UserDetails, Users } from './Users';
import { WarehouseDeliveries } from 'pages/WarehouseDeliveries';
import { WarehouseDeliveryDetails } from 'pages/WarehouseDeliveries/WarehouseDeliveryDetails';
import { WarehouseReturnDetails } from 'pages/WarehouseReturn/details/WarehouseReturnDetails';
import { WarehouseReturns } from 'pages/WarehouseReturn';

function AppRoot() {
    return (
        <Routes>
            {/* Public pages — accessible without authentication */}
            <Route path={PAGES.supplyOrderConfirmation.url} element={<SupplyOrderConfirmation />} />
            {/* Legacy URL kept for emails sent before the kebab-case migration */}
            <Route path="/supplyOrderConfirmation/:token" element={<SupplyOrderConfirmation />} />
            <Route
                path={PAGES.advertisementScreenConfigLoggedOut.url}
                element={<AdvertisementScreenConfigLoggedOut />}
            />

            {/* Guest-only pages — redirect to home if already authenticated */}
            <Route
                path="/login"
                element={
                    <GuestGuard>
                        <Login />
                    </GuestGuard>
                }
            />
            <Route
                path={PAGES.forgotPassword.url}
                element={
                    <GuestGuard>
                        <ForgotPassword />
                    </GuestGuard>
                }
            />
            <Route
                path={PAGES.resetPassword.url}
                element={
                    <GuestGuard>
                        <ResetPassword />
                    </GuestGuard>
                }
            />
            <Route
                path={PAGES.setPassword.url}
                element={
                    <GuestGuard>
                        <SetPassword />
                    </GuestGuard>
                }
            />
            <Route
                path={PAGES.passwordDefined.url}
                element={
                    <GuestGuard>
                        <PasswordDefined />
                    </GuestGuard>
                }
            />

            {/* Private pages — require authentication */}
            <Route
                element={
                    <AuthGuard>
                        <PrivateLayout />
                    </AuthGuard>
                }
            >
                <Route path={PAGES.advertisementScreenConfig.url} element={<AdvertisementScreenConfig />} />
                <Route path={PAGES.badges.url} element={<Badges />} />
                <Route path={PAGES.badgeCreate.url} element={<BadgeCreate />} />
                <Route path={PAGES.badgeUpdate.url} element={<BadgeUpdate />} />
                <Route path={PAGES.bakeryRecords.url} element={<BakeryRecords />} />
                <Route path={PAGES.bundleDetails.url} element={<BundleDetails />} />
                <Route path={PAGES.bundles.url} element={<Bundles />} />
                <Route path={PAGES.categories.url} element={<Categories />} />
                <Route path={PAGES.comments.url} element={<CommentsPage />} />
                <Route path={PAGES.categoryCreate.url} element={<CreateCategory />} />
                <Route path={PAGES.categoryDetails.url} element={<CategoryDetails />} />
                <Route path={PAGES.credits.url} element={<CreditsList />} />
                <Route path={PAGES.creditCreate.url} element={<CreditsForm />} />
                <Route path={PAGES.creditDetails.url} element={<CreditDetails />} />
                <Route path={PAGES.deliveries.url} element={<Deliveries />} />
                <Route path={PAGES.deliveriesDailyPhoto.url} element={<DeliveriesDailyPhotos />} />
                <Route path={PAGES.deliveryPlanning.url} element={<DeliveryPlanning />} />
                <Route path={PAGES.deliveryPlanningAudit.url} element={<DeliveryPlanningAudit />} />
                <Route path={PAGES.deliveryPlanningSelectedDate.url} element={<DeliveryPlanning />} />
                <Route path={PAGES.dynamicDeliveryPlanning.url} element={<DynamicDeliveryPlanning />} />
                <Route path={PAGES.dynamicDeliveryPlanningSelectedDate.url} element={<DynamicDeliveryPlanning />} />
                <Route path={PAGES.tours.url} element={<Tournees />} />
                <Route path={PAGES.toursSelectedDate.url} element={<Tournees />} />
                <Route path={PAGES.tourDetails.url} element={<TourDetails />} />
                <Route path={PAGES.deliveryDetails.url} element={<DeliveryDetails />} />
                <Route path={PAGES.expiryDateRecords.url} element={<ExpiryDateRecords />} />
                <Route path={PAGES.equipments.url} element={<Equipments />} />
                <Route path={PAGES.equipmentCreate.url} element={<EquipmentCreate />} />
                <Route path={PAGES.equipmentDetails.url} element={<EquipmentDetails />} />
                <Route path={PAGES.hotDrinksRecipes.url} element={<HotDrinksRecipes />} />
                <Route path={PAGES.hotDrinksRecipeDetails.url} element={<HotDrinksRecipeDetails />} />
                <Route path={PAGES.locations.url} element={<Locations />} />
                <Route path={PAGES.supplierPartnerships.url} element={<SupplierPartnerships />} />
                <Route path={PAGES.menus.url} element={<MicrostoreMenus />} />
                <Route path={PAGES.menusDetails.url} element={<MicrostoreMenusDetails />} />
                <Route path={PAGES.menuProductArrangements.url} element={<MicrostoreMenuProductArrangements />} />
                <Route path={PAGES.menuQuantities.url} element={<MicrostoreMenuQuantities />} />
                <Route path={PAGES.messagesSets.url} element={<MessagesSet />} />
                <Route path={PAGES.messagesSetsCreate.url} element={<MessagesSetCreate />} />
                <Route path={PAGES.messagesSetDetails.url} element={<MessagesSetDetails />} />
                <Route path={PAGES.microstoreColumns.url} element={<MicrostoreColumns />} />
                <Route path={PAGES.microstoreColumnDetails.url} element={<MicrostoreColumnDetails />} />
                <Route path={PAGES.microstoreColumnGroups.url} element={<MicrostoreColumnGroups />} />
                <Route
                    path={PAGES.microstoreColumnGroupTargetsInfo.url}
                    element={<MicrostoreColumnsGroupTargetsInfo />}
                />
                <Route path={PAGES.microstoreColumnGroupDetails.url} element={<MicrostoreColumnGroupDetails />} />
                <Route path={PAGES.microstorePlanogram.url} element={<MicrostorePlanogram />} />
                <Route path={PAGES.microstoreColumnTemplates.url} element={<MicrostoreColumnTemplates />} />
                <Route path={PAGES.microstoreColumnTemplateDetails.url} element={<MicrostoreColumnTemplateDetails />} />
                <Route path={PAGES.nayaxMDBTemplates.url} element={<NayaxMDBTemplates />} />
                <Route path={PAGES.nayaxMDBTemplateDetails.url} element={<NayaxMDBTemplateDetails />} />
                <Route path={PAGES.manualSupplyOrders.url} element={<ManualSupplyOrders />} />
                <Route path={PAGES.mealPlanDefinitions.url} element={<MealPlanDefinitions />} />
                {/* `create` avant `:id`, sinon le paramètre capte le chemin de création. */}
                <Route path={PAGES.mealPlanDefinitionCreate.url} element={<MealPlanDefinitionCreate />} />
                <Route path={PAGES.mealPlanDefinitionDetails.url} element={<MealPlanDefinitionDetails />} />
                <Route path={PAGES.mobilePopups.url} element={<MobilePopupsPage />} />
                <Route path={PAGES.microstoreSnapshot.url} element={<MicrostoreSnapshot />} />
                <Route path={PAGES.notifications.url} element={<Notifications />} />
                <Route path={PAGES.notificationsOrganization.url} element={<NotificationsOrganization />} />
                <Route path={PAGES.notificationsSite.url} element={<NotificationsSite />} />
                <Route path={PAGES.notificationsUser.url} element={<NotificationsUser />} />
                <Route path={PAGES.opsParameters.url} element={<OpsParameters />} />
                <Route path={PAGES.deliveryZones.url} element={<DeliveryZones />} />
                <Route path={PAGES.deliveryZoneDetails.url} element={<DeliveryZoneDetails />} />
                <Route path={PAGES.permissionGroups.url} element={<PermissionGroups />} />
                <Route path={PAGES.permissionGroupDetails.url} element={<PermissionGroupDetails />} />
                <Route path={PAGES.priceInvisibilityRules.url} element={<PriceInvisibilityRules />} />
                <Route path={PAGES.priceInvisibilityRuleDetails.url} element={<PriceInvisibilityRuleDetails />} />
                <Route path={PAGES.orders.url} element={<Orders />} />
                {/* Avant `/order/:orderId`, sinon « create » est lu comme un identifiant. */}
                <Route path={PAGES.orderCreate.url} element={<OrderCreate />} />
                <Route path={PAGES.orderDetails.url} element={<OrderDetails />} />
                <Route path={PAGES.organizations.url} element={<Organizations />} />
                <Route path={PAGES.organizationCreate.url} element={<OrganizationCreate />} />
                <Route path={PAGES.organizationDetails.url} element={<OrganizationDetails />} />
                <Route path={PAGES.totems.url} element={<Totems />} />
                <Route path={PAGES.totemDetails.url} element={<TotemDetails />} />
                <Route path={PAGES.microstoreColumnGroupPage.url} element={<ColumnGroupPage />} />
                <Route path={PAGES.polls.url} element={<Polls />} />
                <Route path={PAGES.pollCreate.url} element={<PollCreate />} />
                <Route path={PAGES.pollDetails.url} element={<PollDetails />} />
                <Route path={PAGES.productPriceRanges.url} element={<ProductPriceRanges />} />
                <Route path={PAGES.productPriceRangeCreate.url} element={<ProductPriceRangeCreate />} />
                <Route path={PAGES.productPriceRangeDetails.url} element={<ProductPriceRangeDetails />} />
                <Route path={PAGES.products.url} element={<Products />} />
                <Route path={PAGES.productCreate.url} element={<ProductCreate />} />
                <Route path={PAGES.productImport.url} element={<ProductCsvImport />} />
                <Route path={PAGES.productDetails.url} element={<ProductDetails />} />
                <Route path={PAGES.productGroupDetails.url} element={<ProductGroupDetails />} />
                <Route path={PAGES.productArrangements.url} element={<ProductArrangements />} />
                <Route path={PAGES.productArrangementDetails.url} element={<ProductArrangementDetails />} />
                <Route path={PAGES.quizzes.url} element={<Quizzes />} />
                <Route path={PAGES.quizCreate.url} element={<QuizCreate />} />
                <Route path={PAGES.quizDetails.url} element={<QuizDetails />} />
                <Route path={PAGES.ritualCreate.url} element={<RitualCreate />} />
                <Route path={PAGES.ritualDetails.url} element={<RitualDetails />} />
                <Route path={PAGES.rituals.url} element={<Rituals />} />
                <Route path={PAGES.ritualTemplates.url} element={<RitualTemplates />} />
                <Route path={PAGES.settings.url} element={<Settings />} />
                <Route path={PAGES.sites.url} element={<Sites />} />
                <Route path={PAGES.siteCreate.url} element={<SiteCreate />} />
                <Route path={PAGES.siteImport.url} element={<SiteCsvImport />} />
                <Route path={PAGES.siteDetails.url} element={<SiteDetails />} />
                <Route path={PAGES.siteMicrostoreColumnGroups.url} element={<SiteMicrostoreColumnGroups />} />
                <Route path={PAGES.siteInventoryStockCreate.url} element={<SiteInventoryStockCreate />} />
                <Route path={PAGES.siteSetups.url} element={<SiteSetups />} />
                <Route path={PAGES.siteSetupDetails.url} element={<SiteSetupDetails />} />
                <Route path={PAGES.siteStocks.url} element={<SiteStocksOverview />} />
                <Route path={PAGES.columnStocks.url} element={<ColumnStocks />} />
                <Route path={PAGES.columnProductStocks.url} element={<ColumnProductStocks />} />
                <Route path={PAGES.stockTransfers.url} element={<StockTransfers />} />
                <Route path={PAGES.siteExternalInventory.url} element={<SiteExternalInventory />} />
                <Route path={PAGES.stockTransferCreate.url} element={<StockTransferCreate />} />
                <Route path={PAGES.stockTransferReturns.url} element={<StockTransferReturns />} />
                <Route path={PAGES.stockTransferDetails.url} element={<StockTransferDetails />} />
                <Route path={PAGES.expiredFutureStockVariations.url} element={<ExpiredFutureStockVariations />} />
                <Route path={PAGES.futureStockVariations.url} element={<FutureStockVariations />} />
                <Route path={PAGES.stocks.url} element={<Stocks />} />
                <Route path={PAGES.stockVariationCreate.url} element={<StockVariationCreate />} />
                <Route path={PAGES.stockVariationMultiCreate.url} element={<StockVariationMultiCreate />} />
                <Route path={PAGES.stockVariationsByDay.url} element={<StockVariationsByDay />} />
                <Route path={PAGES.subventions.url} element={<Subventions />} />
                <Route path={PAGES.subventionCreate.url} element={<SubventionCreate />} />
                <Route path={PAGES.subventionDetails.url} element={<SubventionDetails />} />
                <Route path={PAGES.suppliers.url} element={<Suppliers />} />
                <Route path={PAGES.supplierCreate.url} element={<SupplierCreate />} />
                <Route path={PAGES.supplierDetails.url} element={<SupplierDetails />} />
                <Route path={PAGES.supplyOrders.url} element={<SupplyOrders />} />
                <Route path={PAGES.supplyOrderDetails.url} element={<SupplyOrderDetails />} />
                <Route path={PAGES.supplierDiscounts.url} element={<SupplierDiscountsPage />} />
                <Route path={PAGES.tags.url} element={<Tags />} />
                <Route path={PAGES.tagCreate.url} element={<TagCreate />} />
                <Route path={PAGES.tagUpdate.url} element={<TagUpdate />} />
                <Route path={PAGES.tickets.url} element={<Tickets />} />
                <Route path={PAGES.ticketDetails.url} element={<TicketDetails />} />
                <Route path={PAGES.termsRecords.url} element={<TermsRecords />} />
                <Route path={PAGES.termsRecordCreate.url} element={<TermsRecordCreate />} />
                <Route path={PAGES.trayCapacities.url} element={<TrayCapacities />} />
                <Route path={PAGES.thermometers.url} element={<Thermometers />} />
                <Route path={PAGES.audit.url} element={<Audit />} />
                <Route path={PAGES.auditDetails.url} element={<AuditDetails />} />
                <Route path={PAGES.ntfy.url} element={<Ntfy />} />
                <Route path={PAGES.payments.url} element={<Payments />} />
                <Route path={PAGES.paymentDetails.url} element={<PaymentDetails />} />
                <Route path={PAGES.paymentMethodDetails.url} element={<PaymentMethodDetails />} />
                <Route path={PAGES.transactions.url} element={<Transactions />} />
                <Route path={PAGES.transactionDetails.url} element={<TransactionDetails />} />
                <Route path={PAGES.totemClosedDays.url} element={<TotemClosedDays />} />
                <Route path={PAGES.totemClosedReceptionDays.url} element={<TotemClosedReceptionDays />} />
                <Route path={PAGES.urbantzRoundPresets.url} element={<UrbantzRoundPresets />} />
                <Route path={PAGES.users.url} element={<Users />} />
                <Route path={PAGES.userCreateToteam.url} element={<CreateToteamUser />} />
                <Route path={PAGES.userCreatedTotops.url} element={<TotopsUserCreated />} />
                <Route path={PAGES.userDetails.url} element={<UserDetails />} />
                <Route path={PAGES.basket.url} element={<Basket />} />
                <Route path={PAGES.warehouseDeliveries.url} element={<WarehouseDeliveries />} />
                <Route path={PAGES.warehouseDeliveryDetails.url} element={<WarehouseDeliveryDetails />} />
                <Route path={PAGES.warehouseReturns.url} element={<WarehouseReturns />} />
                <Route path={PAGES.warehouseReturnDetails.url} element={<WarehouseReturnDetails />} />
                <Route index element={<Home />} />
                <Route path="*" element={<NotFound />} />
            </Route>
        </Routes>
    );
}

export const router = createBrowserRouter([{ path: '*', Component: AppRoot }]);
